/**
 * Demo Requests Tab Component
 * Displays demo requests with status management
 */
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { MessageSquare, Mail, ArrowRight, XCircle } from "lucide-react";

export function DemoRequestsTab({ 
  demoRequests, 
  onUpdateStatus, 
  onConvert,
  getRoleBadgeColor,
  getStatusBadgeColor 
}) {
  if (demoRequests.length === 0) {
    return (
      <Card className="bg-[#151A23] border-slate-700">
        <CardContent className="py-12 text-center">
          <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No demo requests yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {demoRequests.map((demo) => (
        <Card key={demo.id} className="bg-[#151A23] border-slate-700">
          <CardContent className="py-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <p className="font-medium text-white">{demo.name}</p>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(demo.status)}`}>
                    {demo.status}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(demo.role)}`}>
                    {demo.role.replace("_", " ")}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-400">
                  <div>
                    <span className="text-slate-500">Email:</span>
                    <p className="text-slate-300">{demo.email}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Organization:</span>
                    <p className="text-slate-300">{demo.organization}</p>
                  </div>
                  {demo.phone && (
                    <div>
                      <span className="text-slate-500">Phone:</span>
                      <p className="text-slate-300">{demo.phone}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-slate-500">Submitted:</span>
                    <p className="text-slate-300">{new Date(demo.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                {demo.message && (
                  <div className="mt-3 p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-sm text-slate-300">{demo.message}</p>
                  </div>
                )}
              </div>
              {demo.status === "pending" && (
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    size="sm"
                    onClick={() => onUpdateStatus(demo.id, "contacted")}
                    className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                    data-testid={`mark-contacted-${demo.id}`}
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Contacted
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onConvert(demo)}
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    data-testid={`convert-demo-${demo.id}`}
                  >
                    <ArrowRight className="w-4 h-4 mr-1" />
                    Convert
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onUpdateStatus(demo.id, "rejected")}
                    className="text-red-400 hover:bg-red-400/10"
                    data-testid={`reject-demo-${demo.id}`}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              )}
              {demo.status === "contacted" && (
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    size="sm"
                    onClick={() => onConvert(demo)}
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                  >
                    <ArrowRight className="w-4 h-4 mr-1" />
                    Convert to User
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
